import { CobaltFormField, CobaltFormFieldHelperText, CobaltFormFieldLabel } from '@cae/cobalt-react/components/form-field';
import { CobaltSectionMessage } from '@cae/cobalt-react/components/section-message';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { CobaltTextInput } from '@cae/cobalt-react/components/text-input';
import { useEditOtherInformationPanel } from '@features/profile/hooks/use-edit-other-information-panel/useEditOtherInformationPanel';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { UnsavedChangesModal } from '@shared/components/unsaved-changes-modal/UnsavedChangesModal';
import * as React from 'react';

import type { EditOtherInformationPanelProps } from './EditOtherInformationPanel.types';

import { EditOtherInformationActionBar } from './edit-other-information-action-bar/EditOtherInformationActionBar';
import styles from './EditOtherInformationPanel.module.css';

const EditOtherInformationPanel = React.memo(
  ({ onClose }: Readonly<EditOtherInformationPanelProps>): React.ReactElement => {
    const {
      formState,
      errors,
      isMobile,
      isExitModalOpen,
      isDirty,
      isPending,
      isError,
      isProfileLoading,
      isProfileError,
      isProfileReady,
      handleFieldChange,
      handleSubmit,
      handleCloseAttempt,
      handleExit,
      handleKeepEditing,
    } = useEditOtherInformationPanel({ onClose });

    return (
      <>
        <CobaltSidePanel
          isOpen
          showCloseButton
          surtitle="Personal information"
          title="Other personal information"
          description="Used to personalize your experience across CAE platforms and communications."
          onClose={handleCloseAttempt}
          actionBar={
            <EditOtherInformationActionBar
              isMobile={isMobile}
              isPending={isPending}
              isProfileReady={isProfileReady}
              isDirty={isDirty}
              onCloseAttempt={handleCloseAttempt}
              onSubmit={handleSubmit}
            />
          }
          data-heap-id="edit-other-information-panel"
          size={isMobile ? 'large' : 'small'}
        >
          <div className={styles.form}>
            {(isPending || (isProfileLoading && !isProfileReady)) && <ContentLoader />}

            {isProfileError && <InlineFeedback type="data-error" />}

            {isError && (
              <CobaltSectionMessage
                variant="danger"
                sectionMessageTitle={
                  <h3>We couldn&apos;t save your information. Please try again.</h3>
                }
                data-heap-id="mutation-error-banner"
              />
            )}

            <CobaltFormField
              id="profile-other-info-nick-name"
              error={errors.nickName}
            >
              <CobaltFormFieldLabel
                htmlFor="profile-other-info-nick-name"
                label="Preferred name (optional)"
              />
              <CobaltTextInput
                id="profile-other-info-nick-name"
                value={formState.nickName}
                onChange={e => handleFieldChange('nickName', e.target.value)}
                disabled={isPending || (isProfileLoading && !isProfileReady)}
              />
              <CobaltFormFieldHelperText helperId="profile-other-info-nick-name-sub">
                {"We'll use your preferred name where possible. "}
              </CobaltFormFieldHelperText>
              {errors.nickName && (
                <CobaltFormFieldHelperText helperId="profile-other-info-nick-name-error">
                  {errors.nickName}
                </CobaltFormFieldHelperText>
              )}
            </CobaltFormField>
          </div>
        </CobaltSidePanel>

        <UnsavedChangesModal
          isOpen={isExitModalOpen}
          confirmLabel="Keep Editing"
          cancelLabel="Exit"
          onConfirm={handleKeepEditing}
          onCancel={handleExit}
        />
      </>
    );
  }
);

EditOtherInformationPanel.displayName = 'EditOtherInformationPanel';

export { EditOtherInformationPanel };
